import {
    type EventStreamLine,
    type RawCommittedEvent,
    type RawPendingEvent,
    type SnapshotLine,
    type StreamCursor,
    type Uuid,
} from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { type MessageCreateRequest } from '@apps/web/features/phoebe_chat_v3/model/interaction_types';
import {
    type ConnectionState,
    type LifecycleEvidence,
    type OptimisticMessage,
    type ProtocolProblem,
    type ScopeDimensions,
    type SequenceReference,
    type TargetedDrainEvidence,
    type TranscriptState,
} from '@apps/web/features/phoebe_chat_v3/model/state';
import eventCatalog from '@tools/agent_event_catalog/catalog.json';

export type TranscriptAction =
    | Readonly<{
          readonly type: 'scope_reset';
          readonly scope: ScopeDimensions;
          readonly nowMs: number;
      }>
    | Readonly<{
          readonly type: 'cycle_started';
          readonly cycleId: string;
          readonly nowMs: number;
      }>
    | Readonly<{
          readonly type: 'connection_changed';
          readonly connection: ConnectionState;
      }>
    | Readonly<{
          readonly type: 'optimistic_send_enqueued';
          readonly operationId: Uuid;
          readonly request: MessageCreateRequest;
          readonly createdAtMs: number;
          readonly sentWhileBusy: boolean;
          readonly supersededCommandIds?: ReadonlyArray<Uuid>;
      }>
    | Readonly<{
          readonly type: 'optimistic_send_posted';
          readonly operationId: Uuid;
      }>
    | Readonly<{
          readonly type: 'optimistic_send_retried';
          readonly operationId: Uuid;
      }>
    | Readonly<{
          readonly type: 'optimistic_send_failed';
          readonly operationId: Uuid;
          readonly errorKind:
              | 'network'
              | 'rejected'
              | 'unauthorized'
              | 'unknown';
      }>
    | Readonly<{
          readonly type: 'foreground_elapsed';
          readonly elapsedMs: number;
          readonly nowMs: number;
      }>;

export type ApplyResult =
    | Readonly<{
          readonly ok: true;
          readonly state: TranscriptState;
          readonly acknowledgedCursor: StreamCursor;
      }>
    | Readonly<{
          readonly ok: false;
          readonly state: TranscriptState;
          readonly acknowledgedCursor: StreamCursor;
          readonly problem: ProtocolProblem;
      }>;

export type ResponseApplyResult = ApplyResult &
    Readonly<{ readonly acceptedLineCount: number }>;

const ORIGIN_CURSOR: StreamCursor = {
    after_offset: -1,
    after_pending_created_at: null,
    after_pending_id: null,
    after_pending_status_updated_at: null,
    after_pending_status_id: null,
};
const KNOWN_EVENT_TYPES: ReadonlySet<string> = new Set(
    eventCatalog.map((entry) => entry.literal),
);
const UNKNOWN_PAYLOAD_FINGERPRINT_BY_EVENT = new WeakMap<
    RawCommittedEvent,
    string
>();
const FNV_128_OFFSET_BASIS = 0x6c62272e07bb014262b821756295c58dn;
const FNV_128_PRIME = 0x0000000001000000000000000000013bn;
const UINT_128_MASK = (1n << 128n) - 1n;

function canonicalJson(value: unknown): string {
    if (Array.isArray(value)) {
        return `[${value.map(canonicalJson).join(',')}]`;
    }
    if (value !== null && typeof value === 'object') {
        const record = value as Readonly<Record<string, unknown>>;
        const properties = Object.keys(record)
            .filter((key) => record[key] !== undefined)
            .sort()
            .map(
                (key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`,
            );
        return `{${properties.join(',')}}`;
    }
    return JSON.stringify(value) ?? 'null';
}

function payloadFingerprint(payload: unknown): string {
    const bytes = new TextEncoder().encode(canonicalJson(payload));
    let fingerprint = FNV_128_OFFSET_BASIS;
    for (const byte of bytes) {
        fingerprint ^= BigInt(byte);
        fingerprint = (fingerprint * FNV_128_PRIME) & UINT_128_MASK;
    }
    return fingerprint.toString(16).padStart(32, '0');
}

function retainedCommittedEvent(event: RawCommittedEvent): RawCommittedEvent {
    if (KNOWN_EVENT_TYPES.has(event.type)) {
        return event;
    }
    // An unfamiliar future event must not park unreviewed server text in
    // client memory. A later renderer or telemetry change could surface it.
    const retainedEvent = { ...event, payload: null };
    // Replay integrity still needs the unknown payload's identity. Keep only
    // a fixed-size fingerprint outside renderable and serialized state.
    UNKNOWN_PAYLOAD_FINGERPRINT_BY_EVENT.set(
        retainedEvent,
        payloadFingerprint(event.payload),
    );
    return retainedEvent;
}

function sameCommittedEvent(
    left: RawCommittedEvent | undefined,
    right: RawCommittedEvent,
): boolean {
    return (
        left !== undefined &&
        equal(left, right) &&
        UNKNOWN_PAYLOAD_FINGERPRINT_BY_EVENT.get(left) ===
            UNKNOWN_PAYLOAD_FINGERPRINT_BY_EVENT.get(right)
    );
}

class CopyOnWriteMap<Key, Value> {
    private draft: Map<Key, Value> | null = null;

    public constructor(
        private readonly source: ReadonlyMap<Key, Value>,
        private readonly onWrite: () => void,
    ) {}

    public get(key: Key): Value | undefined {
        return (this.draft ?? this.source).get(key);
    }

    public has(key: Key): boolean {
        return (this.draft ?? this.source).has(key);
    }

    public entries(): IterableIterator<[Key, Value]> {
        return (this.draft ?? this.source).entries();
    }

    public values(): IterableIterator<Value> {
        return (this.draft ?? this.source).values();
    }

    public set(key: Key, value: Value): void {
        this.writable().set(key, value);
    }

    public delete(key: Key): void {
        if (this.has(key)) {
            this.writable().delete(key);
        }
    }

    public value(): ReadonlyMap<Key, Value> {
        return this.draft ?? this.source;
    }

    private writable(): Map<Key, Value> {
        if (this.draft === null) {
            this.draft = new Map(this.source);
            this.onWrite();
        }
        return this.draft;
    }
}

class CopyOnWriteOrder<Value> {
    private draft: Array<Value> | null = null;

    public constructor(
        private readonly source: ReadonlyArray<Value>,
        private readonly onWrite: () => void,
    ) {}

    public includes(value: Value): boolean {
        return (this.draft ?? this.source).includes(value);
    }

    public push(value: Value): void {
        if (!this.includes(value)) {
            this.writable().push(value);
        }
    }

    public value(compare?: (left: Value, right: Value) => number) {
        if (compare !== undefined && this.draft !== null) {
            this.draft.sort(compare);
        }
        return this.draft ?? this.source;
    }

    private writable(): Array<Value> {
        if (this.draft === null) {
            this.draft = Array.from(this.source);
            this.onWrite();
        }
        return this.draft;
    }
}

class TranscriptDraft {
    public changed = false;
    public sequenceChanged = false;
    public activeCycleId: string | null;
    public cycleStartCursor: StreamCursor;
    public cursor: StreamCursor;
    public runState: TranscriptState['runState'];
    public lifecycle: LifecycleEvidence;
    public connection: ConnectionState;
    public lastTransportLineAtMs: number | null;
    public lastDoneAtMs: number | null;
    public lastDoneReason: TranscriptState['lastDoneReason'];
    public protocolProblem: ProtocolProblem | null;
    public protocolReplayFingerprint: string | null;
    public protocolReplayCount: 0 | 1;
    public consecutiveNoProgressCycleCount: number;
    public readonly committedById: CopyOnWriteMap<Uuid, RawCommittedEvent>;
    public readonly eventIdByOffset: CopyOnWriteMap<number, Uuid>;
    public readonly committedOrder: CopyOnWriteOrder<Uuid>;
    public readonly pendingById: CopyOnWriteMap<Uuid, RawPendingEvent>;
    public readonly pendingOrder: CopyOnWriteOrder<Uuid>;
    public readonly terminalPendingById: CopyOnWriteMap<Uuid, RawPendingEvent>;
    public readonly committedIdBySourcePendingId: CopyOnWriteMap<Uuid, Uuid>;
    public readonly sourcePendingIdByCommittedId: CopyOnWriteMap<Uuid, Uuid>;
    public readonly operationIdByPendingId: CopyOnWriteMap<Uuid, Uuid>;
    public readonly operationIdByCommittedId: CopyOnWriteMap<Uuid, Uuid>;
    public readonly optimisticByOperationId: CopyOnWriteMap<
        Uuid,
        OptimisticMessage
    >;
    public readonly targetedDrainEvidenceByPendingId: CopyOnWriteMap<
        Uuid,
        TargetedDrainEvidence
    >;
    public readonly recoveryByPendingId: CopyOnWriteMap<
        Uuid,
        TranscriptState['missingDrainedTargetRecoveryByPendingId'] extends ReadonlyMap<
            Uuid,
            infer Recovery
        >
            ? Recovery
            : never
    >;

    public constructor(public readonly base: TranscriptState) {
        const sequenceWrite = () => {
            this.changed = true;
            this.sequenceChanged = true;
        };
        const stateWrite = () => {
            this.changed = true;
        };
        this.activeCycleId = base.activeCycleId;
        this.cycleStartCursor = base.cycleStartCursor;
        this.cursor = base.cursor;
        this.runState = base.runState;
        this.lifecycle = base.lifecycle;
        this.connection = base.connection;
        this.lastTransportLineAtMs = base.lastTransportLineAtMs;
        this.lastDoneAtMs = base.lastDoneAtMs;
        this.lastDoneReason = base.lastDoneReason;
        this.protocolProblem = base.protocolProblem;
        this.protocolReplayFingerprint = base.protocolReplayFingerprint;
        this.protocolReplayCount = base.protocolReplayCount;
        this.consecutiveNoProgressCycleCount =
            base.consecutiveNoProgressCycleCount;
        this.committedById = new CopyOnWriteMap(
            base.committedById,
            sequenceWrite,
        );
        this.eventIdByOffset = new CopyOnWriteMap(
            base.eventIdByOffset,
            stateWrite,
        );
        this.committedOrder = new CopyOnWriteOrder(
            base.committedOrder,
            sequenceWrite,
        );
        this.pendingById = new CopyOnWriteMap(base.pendingById, sequenceWrite);
        this.pendingOrder = new CopyOnWriteOrder(
            base.pendingOrder,
            sequenceWrite,
        );
        this.terminalPendingById = new CopyOnWriteMap(
            base.terminalPendingById,
            stateWrite,
        );
        this.committedIdBySourcePendingId = new CopyOnWriteMap(
            base.committedIdBySourcePendingId,
            sequenceWrite,
        );
        this.sourcePendingIdByCommittedId = new CopyOnWriteMap(
            base.sourcePendingIdByCommittedId,
            sequenceWrite,
        );
        this.operationIdByPendingId = new CopyOnWriteMap(
            base.operationIdByPendingId,
            sequenceWrite,
        );
        this.operationIdByCommittedId = new CopyOnWriteMap(
            base.operationIdByCommittedId,
            sequenceWrite,
        );
        this.optimisticByOperationId = new CopyOnWriteMap(
            base.optimisticByOperationId,
            sequenceWrite,
        );
        this.targetedDrainEvidenceByPendingId = new CopyOnWriteMap(
            base.targetedDrainEvidenceByPendingId,
            stateWrite,
        );
        this.recoveryByPendingId = new CopyOnWriteMap(
            base.missingDrainedTargetRecoveryByPendingId,
            stateWrite,
        );
    }

    public markChanged(): void {
        this.changed = true;
    }

    public finalize(): TranscriptState {
        if (!this.changed) {
            return this.base;
        }
        const pendingOrder = this.pendingOrder.value((left, right) => {
            const leftEvent = this.pendingById.get(left);
            const rightEvent = this.pendingById.get(right);
            return leftEvent === undefined || rightEvent === undefined
                ? left.localeCompare(right)
                : comparePending(leftEvent, rightEvent);
        });
        const sequence = this.sequenceChanged
            ? deriveSequence(this, pendingOrder)
            : this.base.sequence;
        return {
            scope: this.base.scope,
            activeCycleId: this.activeCycleId,
            cycleStartCursor: this.cycleStartCursor,
            cursor: this.cursor,
            committedById: this.committedById.value(),
            eventIdByOffset: this.eventIdByOffset.value(),
            committedOrder: this.committedOrder.value(),
            pendingById: this.pendingById.value(),
            pendingOrder,
            terminalPendingById: this.terminalPendingById.value(),
            committedIdBySourcePendingId:
                this.committedIdBySourcePendingId.value(),
            sourcePendingIdByCommittedId:
                this.sourcePendingIdByCommittedId.value(),
            operationIdByPendingId: this.operationIdByPendingId.value(),
            operationIdByCommittedId: this.operationIdByCommittedId.value(),
            optimisticByOperationId: this.optimisticByOperationId.value(),
            targetedDrainEvidenceByPendingId:
                this.targetedDrainEvidenceByPendingId.value(),
            missingDrainedTargetRecoveryByPendingId:
                this.recoveryByPendingId.value(),
            sequence,
            runState: this.runState,
            lifecycle: this.lifecycle,
            connection: this.connection,
            lastTransportLineAtMs: this.lastTransportLineAtMs,
            lastDoneAtMs: this.lastDoneAtMs,
            lastDoneReason: this.lastDoneReason,
            protocolProblem: this.protocolProblem,
            protocolReplayFingerprint: this.protocolReplayFingerprint,
            protocolReplayCount: this.protocolReplayCount,
            consecutiveNoProgressCycleCount:
                this.consecutiveNoProgressCycleCount,
        };
    }
}

// Server timestamps are fixed-width UTC µs, so string order is time order.
function compareOrdinal(left: string, right: string): number {
    return left < right ? -1 : left > right ? 1 : 0;
}

function comparePending(left: RawPendingEvent, right: RawPendingEvent): number {
    return left.created_at === right.created_at
        ? compareOrdinal(left.id, right.id)
        : compareOrdinal(left.created_at, right.created_at);
}

function equal(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) {
        return true;
    }
    if (Array.isArray(left) && Array.isArray(right)) {
        return (
            left.length === right.length &&
            left.every((value, index) => equal(value, right[index]))
        );
    }
    if (
        typeof left !== 'object' ||
        left === null ||
        typeof right !== 'object' ||
        right === null
    ) {
        return false;
    }
    const leftRecord = left as Readonly<Record<string, unknown>>;
    const rightRecord = right as Readonly<Record<string, unknown>>;
    const keys = Object.keys(leftRecord).sort();
    return (
        equal(keys, Object.keys(rightRecord).sort()) &&
        keys.every((key) => equal(leftRecord[key], rightRecord[key]))
    );
}

function cursorIsValid(cursor: StreamCursor): boolean {
    return (
        Number.isInteger(cursor.after_offset) &&
        cursor.after_offset >= -1 &&
        (cursor.after_pending_created_at === null) ===
            (cursor.after_pending_id === null) &&
        (cursor.after_pending_status_updated_at === null) ===
            (cursor.after_pending_status_id === null)
    );
}

function compareCursor(
    left: StreamCursor,
    right: StreamCursor,
): Readonly<{
    readonly committed: number;
    readonly pending: number;
    readonly status: number;
}> {
    const comparePair = (
        leftAt: string | null,
        leftId: string | null,
        rightAt: string | null,
        rightId: string | null,
    ) => {
        if (leftAt === rightAt) {
            return compareOrdinal(leftId ?? '', rightId ?? '');
        }
        return compareOrdinal(leftAt ?? '', rightAt ?? '');
    };
    return {
        committed: Math.sign(left.after_offset - right.after_offset),
        pending: Math.sign(
            comparePair(
                left.after_pending_created_at,
                left.after_pending_id,
                right.after_pending_created_at,
                right.after_pending_id,
            ),
        ),
        status: Math.sign(
            comparePair(
                left.after_pending_status_updated_at,
                left.after_pending_status_id,
                right.after_pending_status_updated_at,
                right.after_pending_status_id,
            ),
        ),
    };
}

function cursorProblem(
    actual: StreamCursor,
    current: StreamCursor,
    expected: StreamCursor,
): ProtocolProblem['reason'] | null {
    if (!cursorIsValid(actual)) {
        return 'invalid_line';
    }
    if (equal(actual, expected)) {
        return null;
    }
    return Object.values(compareCursor(actual, current)).some(
        (part) => part < 0,
    )
        ? 'cursor_regression'
        : 'cursor_jump';
}

function replayCursorProblem(
    actual: StreamCursor,
    current: StreamCursor,
): ProtocolProblem['reason'] | null {
    if (!cursorIsValid(actual)) {
        return 'invalid_line';
    }
    return Object.values(compareCursor(actual, current)).some(
        (part) => part > 0,
    )
        ? 'cursor_jump'
        : null;
}

function makeProblem(
    draft: TranscriptDraft,
    reason: ProtocolProblem['reason'],
    lineType: string | null,
    eventType: string | null = null,
): ProtocolProblem {
    return {
        cycleId: draft.activeCycleId ?? 'no-active-cycle',
        reason,
        lineType,
        eventType,
    };
}

function operationId(externalReference: string | null): Uuid | null {
    const prefix = 'api_chat_send:';
    return externalReference?.startsWith(prefix) === true &&
        externalReference.length > prefix.length
        ? externalReference.slice(prefix.length)
        : null;
}

function duplicateValue<Key>(
    entries: IterableIterator<[Key, Uuid]>,
    value: Uuid,
    except: Key,
): boolean {
    for (const [key, candidate] of entries) {
        if (key !== except && candidate === value) {
            return true;
        }
    }
    return false;
}

function optimisticOccurrence(operation: Uuid): `local-operation:${string}` {
    return `local-operation:${operation}`;
}

function deriveSequence(
    draft: TranscriptDraft,
    pendingOrder: ReadonlyArray<Uuid>,
): ReadonlyArray<SequenceReference> {
    const sequence: Array<SequenceReference> = [];
    for (const eventId of draft.committedOrder.value()) {
        const event = draft.committedById.get(eventId);
        if (event === undefined) {
            continue;
        }
        const sourcePendingId = draft.sourcePendingIdByCommittedId.get(eventId);
        const operation =
            draft.operationIdByCommittedId.get(eventId) ??
            (sourcePendingId === undefined
                ? undefined
                : draft.operationIdByPendingId.get(sourcePendingId));
        sequence.push({
            kind: 'committed',
            occurrenceId:
                operation === undefined
                    ? sourcePendingId === undefined
                        ? `committed-event:${eventId}`
                        : `pending-event:${sourcePendingId}`
                    : optimisticOccurrence(operation),
            eventId,
            eventOffset: event.event_offset,
        });
    }
    for (const pendingEventId of pendingOrder) {
        const pending = draft.pendingById.get(pendingEventId);
        const committedId =
            draft.committedIdBySourcePendingId.get(pendingEventId);
        if (
            pending === undefined ||
            (committedId !== undefined &&
                draft.committedById.has(committedId)) ||
            (pending.status === 'drained' &&
                pending.drained_into_event_id === null)
        ) {
            continue;
        }
        const operation = draft.operationIdByPendingId.get(pendingEventId);
        sequence.push({
            kind: 'pending',
            occurrenceId:
                operation === undefined
                    ? `pending-event:${pendingEventId}`
                    : optimisticOccurrence(operation),
            pendingEventId,
            createdAt: pending.created_at,
        });
    }
    const optimistic = Array.from(draft.optimisticByOperationId.values()).sort(
        (left, right) =>
            left.createdAtMs - right.createdAtMs ||
            left.operationId.localeCompare(right.operationId),
    );
    for (const message of optimistic) {
        if (
            message.matchedPendingId === null &&
            message.matchedCommittedId === null
        ) {
            sequence.push({
                kind: 'optimistic',
                occurrenceId: message.occurrenceId,
                operationId: message.operationId,
                createdAtMs: message.createdAtMs,
            });
        }
    }
    return sequence;
}

function operationConflict(
    draft: TranscriptDraft,
    pendingId: Uuid | null,
    committedId: Uuid | null,
    operation: Uuid,
): boolean {
    const optimistic = draft.optimisticByOperationId.get(operation);
    return (
        (pendingId !== null &&
            (duplicateValue(
                draft.operationIdByPendingId.entries(),
                operation,
                pendingId,
            ) ||
                (optimistic?.matchedPendingId !== null &&
                    optimistic?.matchedPendingId !== undefined &&
                    optimistic.matchedPendingId !== pendingId))) ||
        (committedId !== null &&
            (duplicateValue(
                draft.operationIdByCommittedId.entries(),
                operation,
                committedId,
            ) ||
                (optimistic?.matchedCommittedId !== null &&
                    optimistic?.matchedCommittedId !== undefined &&
                    optimistic.matchedCommittedId !== committedId)))
    );
}

function linkOperation(
    draft: TranscriptDraft,
    pendingId: Uuid | null,
    committedId: Uuid | null,
    operation: Uuid,
): void {
    if (pendingId !== null) {
        draft.operationIdByPendingId.set(pendingId, operation);
    }
    if (committedId !== null) {
        draft.operationIdByCommittedId.set(committedId, operation);
    }
    const optimistic = draft.optimisticByOperationId.get(operation);
    if (optimistic !== undefined) {
        draft.optimisticByOperationId.set(operation, {
            ...optimistic,
            matchedPendingId: pendingId ?? optimistic.matchedPendingId,
            matchedCommittedId: committedId ?? optimistic.matchedCommittedId,
        });
    }
}

function applyCommitted(
    draft: TranscriptDraft,
    event: RawCommittedEvent,
    cursor: StreamCursor,
): ProtocolProblem | null {
    const retainedEvent = retainedCommittedEvent(event);
    const existing = draft.committedById.get(event.id);
    if (existing !== undefined) {
        if (
            existing.event_offset !== event.event_offset ||
            !sameCommittedEvent(existing, retainedEvent)
        ) {
            return makeProblem(draft, 'event_id_conflict', 'event', event.type);
        }
        const reason = replayCursorProblem(cursor, draft.cursor);
        if (reason !== null) {
            return makeProblem(draft, reason, 'event', event.type);
        }
        return null;
    }
    if (event.event_offset < 0) {
        return makeProblem(draft, 'invalid_line', 'event', event.type);
    }
    const offsetOwner = draft.eventIdByOffset.get(event.event_offset);
    if (offsetOwner !== undefined) {
        return makeProblem(draft, 'event_offset_conflict', 'event', event.type);
    }
    const committedOrder = draft.committedOrder.value();
    const lastId = committedOrder[committedOrder.length - 1];
    const lastOffset =
        lastId === undefined
            ? -1
            : (draft.committedById.get(lastId)?.event_offset ?? -1);
    if (event.event_offset <= lastOffset) {
        return makeProblem(
            draft,
            'unseen_event_below_tail',
            'event',
            event.type,
        );
    }
    const expected = { ...draft.cursor, after_offset: event.event_offset };
    const cursorReason = cursorProblem(cursor, draft.cursor, expected);
    if (cursorReason !== null) {
        return makeProblem(draft, cursorReason, 'event', event.type);
    }
    const pendingId = event.source_pending_event_id;
    const knownTarget =
        pendingId === null
            ? undefined
            : draft.committedIdBySourcePendingId.get(pendingId);
    const knownSource = draft.sourcePendingIdByCommittedId.get(event.id);
    const terminal =
        pendingId === null
            ? undefined
            : draft.terminalPendingById.get(pendingId);
    if (
        (knownTarget !== undefined && knownTarget !== event.id) ||
        (knownSource !== undefined && knownSource !== pendingId) ||
        (terminal !== undefined && terminal.drained_into_event_id !== event.id)
    ) {
        return makeProblem(
            draft,
            'source_pending_conflict',
            'event',
            event.type,
        );
    }
    const directOperation = operationId(event.external_ref);
    const pendingOperation =
        pendingId === null
            ? undefined
            : draft.operationIdByPendingId.get(pendingId);
    if (
        directOperation !== null &&
        pendingOperation !== undefined &&
        directOperation !== pendingOperation
    ) {
        return makeProblem(
            draft,
            'operation_identity_conflict',
            'event',
            event.type,
        );
    }
    const operation = directOperation ?? pendingOperation ?? null;
    if (
        operation !== null &&
        operationConflict(draft, pendingId, event.id, operation)
    ) {
        return makeProblem(
            draft,
            'operation_identity_conflict',
            'event',
            event.type,
        );
    }
    draft.committedById.set(event.id, retainedEvent);
    draft.eventIdByOffset.set(event.event_offset, event.id);
    draft.committedOrder.push(event.id);
    if (pendingId !== null) {
        draft.committedIdBySourcePendingId.set(pendingId, event.id);
        draft.sourcePendingIdByCommittedId.set(event.id, pendingId);
        draft.targetedDrainEvidenceByPendingId.delete(pendingId);
        draft.recoveryByPendingId.delete(pendingId);
    }
    if (operation !== null) {
        linkOperation(draft, pendingId, event.id, operation);
    }
    draft.cursor = cursor;
    draft.markChanged();
    return null;
}

function applyPending(
    draft: TranscriptDraft,
    pending: RawPendingEvent,
    cursor: StreamCursor,
    update: boolean,
): ProtocolProblem | null {
    const lineType = update ? 'pending_event_update' : 'pending_event';
    const existing = draft.pendingById.get(pending.id);
    const terminal = draft.terminalPendingById.get(pending.id);
    const exact = existing !== undefined && equal(existing, pending);
    const insertionPosition = {
        ...draft.cursor,
        after_pending_created_at: pending.created_at,
        after_pending_id: pending.id,
    };
    const statusPosition =
        pending.status_updated_at === null
            ? null
            : {
                  ...draft.cursor,
                  after_pending_status_updated_at: pending.status_updated_at,
                  after_pending_status_id: pending.id,
              };
    const positionAlreadyAcknowledged = update
        ? statusPosition !== null &&
          compareCursor(statusPosition, draft.cursor).status <= 0
        : compareCursor(insertionPosition, draft.cursor).pending <= 0;
    const governedInsertionReplay =
        !update &&
        terminal !== undefined &&
        compareCursor(insertionPosition, draft.cursor).pending <= 0;
    const replay =
        (exact && positionAlreadyAcknowledged) || governedInsertionReplay;
    const existingTerminalIsAtLeastAsNew =
        update &&
        terminal?.status_updated_at !== null &&
        terminal?.status_updated_at !== undefined &&
        pending.status_updated_at !== null &&
        pending.status_updated_at <= terminal.status_updated_at;
    if (
        update &&
        terminal !== undefined &&
        !exact &&
        statusPosition !== null &&
        (positionAlreadyAcknowledged || existingTerminalIsAtLeastAsNew)
    ) {
        const reason = replayCursorProblem(cursor, draft.cursor);
        return reason === null
            ? null
            : makeProblem(draft, reason, lineType, pending.type);
    }
    const identitySeen = update
        ? terminal !== undefined
        : existing !== undefined;
    if (positionAlreadyAcknowledged && !replay && !identitySeen) {
        return makeProblem(draft, 'cursor_regression', lineType, pending.type);
    }
    if (replay) {
        const reason = replayCursorProblem(cursor, draft.cursor);
        if (reason !== null) {
            return makeProblem(draft, reason, lineType, pending.type);
        }
    } else {
        const positionAt = update
            ? pending.status_updated_at
            : pending.created_at;
        if (positionAt === null) {
            return makeProblem(draft, 'invalid_line', lineType, pending.type);
        }
        const expected = update
            ? {
                  ...draft.cursor,
                  after_pending_status_updated_at: positionAt,
                  after_pending_status_id: pending.id,
              }
            : {
                  ...draft.cursor,
                  after_pending_created_at: positionAt,
                  after_pending_id: pending.id,
              };
        const cursorReason = cursorProblem(cursor, draft.cursor, expected);
        if (cursorReason !== null) {
            return makeProblem(draft, cursorReason, lineType, pending.type);
        }
        if (
            existing !== undefined &&
            !update &&
            !exact &&
            terminal === undefined
        ) {
            return makeProblem(draft, 'invalid_line', lineType, pending.type);
        }
    }
    const committedTarget = draft.committedIdBySourcePendingId.get(pending.id);
    const terminalTarget =
        pending.status === 'drained'
            ? pending.drained_into_event_id
            : !update && terminal?.status === 'drained'
              ? terminal.drained_into_event_id
              : null;
    const linkedCommittedId = terminalTarget ?? committedTarget ?? null;
    if (
        (terminalTarget !== null &&
            committedTarget !== undefined &&
            terminalTarget !== committedTarget) ||
        (pending.status !== 'pending' &&
            terminalTarget === null &&
            committedTarget !== undefined)
    ) {
        return makeProblem(
            draft,
            'source_pending_conflict',
            lineType,
            pending.type,
        );
    }
    if (terminalTarget !== null) {
        const target = draft.committedById.get(terminalTarget);
        if (
            target !== undefined &&
            target.source_pending_event_id !== pending.id
        ) {
            return makeProblem(
                draft,
                'source_pending_conflict',
                lineType,
                pending.type,
            );
        }
    }
    const directOperation = operationId(pending.external_ref);
    const knownOperation = draft.operationIdByPendingId.get(pending.id);
    const targetOperation =
        linkedCommittedId === null
            ? undefined
            : draft.operationIdByCommittedId.get(linkedCommittedId);
    if (
        (directOperation !== null &&
            knownOperation !== undefined &&
            directOperation !== knownOperation) ||
        (directOperation !== null &&
            targetOperation !== undefined &&
            directOperation !== targetOperation)
    ) {
        return makeProblem(
            draft,
            'operation_identity_conflict',
            lineType,
            pending.type,
        );
    }
    const operation =
        directOperation ?? knownOperation ?? targetOperation ?? null;
    if (
        operation !== null &&
        operationConflict(draft, pending.id, linkedCommittedId, operation)
    ) {
        return makeProblem(
            draft,
            'operation_identity_conflict',
            lineType,
            pending.type,
        );
    }
    if (replay && operation === (knownOperation ?? null)) {
        return null;
    }
    if (terminal === undefined || update) {
        draft.pendingById.set(pending.id, pending);
        draft.pendingOrder.push(pending.id);
        if (pending.status !== 'pending') {
            draft.terminalPendingById.set(pending.id, pending);
        }
    }
    if (terminalTarget !== null) {
        draft.committedIdBySourcePendingId.set(pending.id, terminalTarget);
        draft.sourcePendingIdByCommittedId.set(terminalTarget, pending.id);
        if (!draft.committedById.has(terminalTarget)) {
            const evidence = draft.targetedDrainEvidenceByPendingId.get(
                pending.id,
            );
            if (evidence === undefined) {
                draft.targetedDrainEvidenceByPendingId.set(pending.id, {
                    targetCommittedId: terminalTarget,
                    completeCatchUpCycleCount: 0,
                    foregroundElapsedMs: 0,
                    issueReported: false,
                });
            }
        }
    } else if (pending.status !== 'pending') {
        draft.targetedDrainEvidenceByPendingId.delete(pending.id);
        draft.recoveryByPendingId.delete(pending.id);
    }
    if (operation !== null) {
        linkOperation(draft, pending.id, linkedCommittedId, operation);
    }
    if (!replay) {
        draft.cursor = cursor;
        draft.markChanged();
    }
    return null;
}

function updateLifecycle(
    previous: TranscriptState['runState'],
    next: NonNullable<TranscriptState['runState']>,
    lifecycle: LifecycleEvidence,
    nowMs: number,
): LifecycleEvidence {
    const strongBefore =
        previous?.is_generating === true || previous?.has_active_lease === true;
    const strongNow = next.is_generating || next.has_active_lease;
    const hasActivity =
        next.has_pending_work || next.is_generating || next.has_active_lease;
    const hasWeakWork =
        next.status === 'running' ||
        next.has_pending_work ||
        next.is_generating;
    const terminal =
        next.status === 'completed' ||
        next.status === 'failed' ||
        next.status === 'cancelled';
    return {
        ...lifecycle,
        latestTerminalAtMs:
            terminal && !hasActivity
                ? (lifecycle.latestTerminalAtMs ?? nowMs)
                : lifecycle.latestTerminalAtMs,
        interruptRequestedAtMs: next.interrupt_requested
            ? previous?.interrupt_requested === true
                ? lifecycle.interruptRequestedAtMs
                : nowMs
            : null,
        pendingWorkObservedAtMs: next.has_pending_work
            ? previous?.has_pending_work === true
                ? lifecycle.pendingWorkObservedAtMs
                : nowMs
            : null,
        runningObservedAtMs:
            next.status === 'running'
                ? previous?.status === 'running'
                    ? lifecycle.runningObservedAtMs
                    : nowMs
                : null,
        strongActivityBeganAtMs: strongNow
            ? strongBefore
                ? lifecycle.strongActivityBeganAtMs
                : nowMs
            : null,
        strongHandoffBeganAtMs:
            !strongNow &&
            strongBefore &&
            (next.status === 'running' ||
                next.has_pending_work ||
                next.is_generating)
                ? nowMs
                : strongNow || !hasWeakWork
                  ? null
                  : lifecycle.strongHandoffBeganAtMs,
    };
}

function advanceTargetEvidence(
    draft: TranscriptDraft,
    cycleCount: number,
    elapsedMs: number,
): void {
    for (const [pendingId, evidence] of Array.from(
        draft.targetedDrainEvidenceByPendingId.entries(),
    )) {
        if (draft.committedById.has(evidence.targetCommittedId)) {
            draft.targetedDrainEvidenceByPendingId.delete(pendingId);
            draft.recoveryByPendingId.delete(pendingId);
            continue;
        }
        const next = {
            ...evidence,
            completeCatchUpCycleCount:
                evidence.completeCatchUpCycleCount + cycleCount,
            foregroundElapsedMs: evidence.foregroundElapsedMs + elapsedMs,
        };
        const shouldReport =
            !next.issueReported &&
            next.completeCatchUpCycleCount >= 2 &&
            next.foregroundElapsedMs >= 30_000;
        draft.targetedDrainEvidenceByPendingId.set(pendingId, {
            ...next,
            issueReported: next.issueReported || shouldReport,
        });
        if (shouldReport) {
            draft.recoveryByPendingId.set(pendingId, {
                kind: 'missing_drained_target',
                pendingEventId: pendingId,
                targetCommittedId: evidence.targetCommittedId,
                action: 'Reload conversation',
            });
        }
    }
}

function applyControlLine(
    draft: TranscriptDraft,
    line: Exclude<
        EventStreamLine,
        | SnapshotLine
        | { readonly type: 'event' }
        | { readonly type: 'pending_event' }
        | {
              readonly type: 'pending_event_update';
          }
    >,
    nowMs: number,
): ProtocolProblem | null {
    const reason = cursorProblem(line.cursor, draft.cursor, draft.cursor);
    if (reason !== null) {
        return makeProblem(draft, reason, line.type);
    }
    if (line.type === 'run_state') {
        if (
            draft.runState !== null &&
            line.run_state.updated_at < draft.runState.updated_at
        ) {
            return null;
        }
        if (!equal(draft.runState, line.run_state)) {
            draft.lifecycle = updateLifecycle(
                draft.runState,
                line.run_state,
                draft.lifecycle,
                nowMs,
            );
            draft.runState = line.run_state;
            draft.lastTransportLineAtMs = nowMs;
            draft.markChanged();
        }
        return null;
    }
    if (line.type === 'heartbeat') {
        if (draft.lastTransportLineAtMs !== nowMs) {
            draft.lastTransportLineAtMs = nowMs;
            draft.markChanged();
        }
        return null;
    }
    if (line.type === 'done') {
        if (
            line.reason === 'page_full' &&
            equal(draft.cursor, draft.cycleStartCursor)
        ) {
            return makeProblem(draft, 'page_full_without_progress', line.type);
        }
        const madeProgress = !equal(draft.cursor, draft.cycleStartCursor);
        draft.lastDoneAtMs = nowMs;
        draft.lastDoneReason = line.reason;
        draft.lastTransportLineAtMs = nowMs;
        draft.consecutiveNoProgressCycleCount = madeProgress
            ? 0
            : draft.consecutiveNoProgressCycleCount + 1;
        draft.protocolProblem = null;
        draft.markChanged();
        if (line.reason === 'cycle_complete') {
            advanceTargetEvidence(draft, 1, 0);
        }
        return null;
    }
    return makeProblem(draft, 'invalid_line', null);
}

function applyWireLine(
    draft: TranscriptDraft,
    line: EventStreamLine,
    nowMs: number,
): ProtocolProblem | null {
    if (draft.lastDoneAtMs !== null) {
        return makeProblem(
            draft,
            line.type === 'done' ? 'duplicate_done' : 'line_after_done',
            line.type,
        );
    }
    switch (line.type) {
        case 'snapshot':
            return makeProblem(draft, 'unexpected_snapshot', line.type);
        case 'event': {
            const cursorBefore = draft.cursor;
            const problem = applyCommitted(draft, line.event, line.cursor);
            if (problem === null && !equal(cursorBefore, draft.cursor)) {
                draft.lastTransportLineAtMs = nowMs;
                draft.markChanged();
            }
            return problem;
        }
        case 'pending_event': {
            const cursorBefore = draft.cursor;
            const problem = applyPending(
                draft,
                line.pending_event,
                line.cursor,
                false,
            );
            if (problem === null && !equal(cursorBefore, draft.cursor)) {
                draft.lastTransportLineAtMs = nowMs;
                draft.markChanged();
            }
            return problem;
        }
        case 'pending_event_update': {
            const cursorBefore = draft.cursor;
            const problem = applyPending(
                draft,
                line.pending_event_update,
                line.cursor,
                true,
            );
            if (problem === null && !equal(cursorBefore, draft.cursor)) {
                draft.lastTransportLineAtMs = nowMs;
                draft.markChanged();
            }
            return problem;
        }
        case 'run_state':
        case 'heartbeat':
        case 'done':
            return applyControlLine(draft, line, nowMs);
        default:
            return makeProblem(draft, 'invalid_line', null);
    }
}

function applySnapshotToDraft(
    draft: TranscriptDraft,
    snapshot: SnapshotLine,
    nowMs: number,
): ProtocolProblem | null {
    if (
        !equal(draft.cursor, ORIGIN_CURSOR) ||
        draft.committedOrder.value().length > 0 ||
        draft.pendingOrder.value().length > 0 ||
        draft.runState !== null
    ) {
        return makeProblem(draft, 'unexpected_snapshot', snapshot.type);
    }
    for (const event of snapshot.events) {
        const cursor = { ...draft.cursor, after_offset: event.event_offset };
        const problem = applyCommitted(draft, event, cursor);
        if (problem !== null) {
            return problem;
        }
    }
    for (const pending of snapshot.pending_events) {
        const cursor = {
            ...draft.cursor,
            after_pending_created_at: pending.created_at,
            after_pending_id: pending.id,
        };
        const problem = applyPending(draft, pending, cursor, false);
        if (problem !== null) {
            return problem;
        }
    }
    for (const pending of snapshot.pending_event_updates) {
        if (pending.status_updated_at === null) {
            return makeProblem(draft, 'invalid_line', snapshot.type);
        }
        const cursor = {
            ...draft.cursor,
            after_pending_status_updated_at: pending.status_updated_at,
            after_pending_status_id: pending.id,
        };
        const problem = applyPending(draft, pending, cursor, true);
        if (problem !== null) {
            return problem;
        }
    }
    const reason = cursorProblem(snapshot.cursor, draft.cursor, draft.cursor);
    if (reason !== null) {
        return makeProblem(draft, reason, snapshot.type);
    }
    draft.lastTransportLineAtMs = nowMs;
    draft.markChanged();
    return null;
}

function durableMatches(
    draft: TranscriptDraft,
    operation: Uuid,
): Readonly<{
    readonly pendingId: Uuid | null;
    readonly committedId: Uuid | null;
}> {
    let pendingId: Uuid | null = null;
    let committedId: Uuid | null = null;
    for (const [id, candidate] of draft.operationIdByPendingId.entries()) {
        if (candidate === operation) {
            pendingId = id;
        }
    }
    for (const [id, candidate] of draft.operationIdByCommittedId.entries()) {
        if (candidate === operation) {
            committedId = id;
        }
    }
    return { pendingId, committedId };
}

function applyLocalAction(
    draft: TranscriptDraft,
    action: Exclude<TranscriptAction, { readonly type: 'scope_reset' }>,
): ProtocolProblem | null {
    switch (action.type) {
        case 'cycle_started':
            draft.activeCycleId = action.cycleId;
            draft.cycleStartCursor = draft.cursor;
            draft.lastDoneAtMs = null;
            draft.lastDoneReason = null;
            draft.markChanged();
            return null;
        case 'connection_changed':
            if (!equal(draft.connection, action.connection)) {
                draft.connection = action.connection;
                draft.markChanged();
            }
            return null;
        case 'optimistic_send_enqueued': {
            if (action.operationId !== action.request.client_operation_id) {
                return makeProblem(
                    draft,
                    'operation_identity_conflict',
                    action.type,
                );
            }
            const existing = draft.optimisticByOperationId.get(
                action.operationId,
            );
            if (existing !== undefined) {
                return existing.text === action.request.content &&
                    existing.createdAtMs === action.createdAtMs
                    ? null
                    : makeProblem(
                          draft,
                          'operation_identity_conflict',
                          action.type,
                      );
            }
            const matches = durableMatches(draft, action.operationId);
            draft.optimisticByOperationId.set(action.operationId, {
                operationId: action.operationId,
                occurrenceId: optimisticOccurrence(action.operationId),
                text: action.request.content,
                createdAtMs: action.createdAtMs,
                status: 'posting',
                errorKind: null,
                matchedPendingId: matches.pendingId,
                matchedCommittedId: matches.committedId,
                sentWhileBusy: action.sentWhileBusy,
                supersededCommandIds: action.supersededCommandIds ?? [],
            });
            return null;
        }
        case 'foreground_elapsed':
            if (action.elapsedMs < 0 || !Number.isFinite(action.elapsedMs)) {
                return makeProblem(draft, 'invalid_line', action.type);
            }
            if (draft.connection.kind === 'hidden' || action.elapsedMs === 0) {
                return null;
            }
            advanceTargetEvidence(draft, 0, action.elapsedMs);
            return null;
        case 'optimistic_send_posted':
        case 'optimistic_send_retried':
        case 'optimistic_send_failed': {
            const message = draft.optimisticByOperationId.get(
                action.operationId,
            );
            if (message === undefined) {
                return null;
            }
            draft.optimisticByOperationId.set(action.operationId, {
                ...message,
                status:
                    action.type === 'optimistic_send_posted'
                        ? 'awaiting_echo'
                        : action.type === 'optimistic_send_retried'
                          ? 'posting'
                          : 'failed',
                errorKind:
                    action.type === 'optimistic_send_failed'
                        ? action.errorKind
                        : null,
            });
            return null;
        }
        default:
            return makeProblem(draft, 'invalid_line', null);
    }
}

function isAction(
    line: EventStreamLine | TranscriptAction,
): line is TranscriptAction {
    switch (line.type) {
        case 'scope_reset':
        case 'cycle_started':
        case 'connection_changed':
        case 'optimistic_send_enqueued':
        case 'optimistic_send_posted':
        case 'optimistic_send_retried':
        case 'optimistic_send_failed':
        case 'foreground_elapsed':
            return true;
        default:
            return false;
    }
}

function recordProblem(draft: TranscriptDraft, problem: ProtocolProblem): void {
    const fingerprint = JSON.stringify([
        problem.reason,
        problem.lineType,
        problem.eventType,
    ]);
    draft.protocolProblem = problem;
    draft.protocolReplayCount =
        draft.protocolReplayFingerprint === fingerprint ? 1 : 0;
    draft.protocolReplayFingerprint = fingerprint;
    draft.markChanged();
}

function isExactPendingReplay(
    state: TranscriptState,
    line: EventStreamLine,
): boolean {
    if (line.type !== 'pending_event' && line.type !== 'pending_event_update') {
        return false;
    }
    const update = line.type === 'pending_event_update';
    const pending = update ? line.pending_event_update : line.pending_event;
    if (!equal(state.pendingById.get(pending.id), pending)) {
        return false;
    }
    const positionAt = update ? pending.status_updated_at : pending.created_at;
    if (positionAt === null) {
        return false;
    }
    const position = update
        ? {
              ...state.cursor,
              after_pending_status_updated_at: positionAt,
              after_pending_status_id: pending.id,
          }
        : {
              ...state.cursor,
              after_pending_created_at: positionAt,
              after_pending_id: pending.id,
          };
    const comparison = compareCursor(position, state.cursor);
    const alreadyAcknowledged = update
        ? comparison.status <= 0
        : comparison.pending <= 0;
    return (
        alreadyAcknowledged &&
        replayCursorProblem(line.cursor, state.cursor) === null
    );
}

type ResponseReplayInspection =
    | Readonly<{ readonly kind: 'exact' }>
    | Readonly<{ readonly kind: 'not_replay' }>
    | Readonly<{
          readonly kind: 'event_id_conflict';
          readonly acceptedLineCount: number;
          readonly eventType: string;
      }>;

function inspectResponseReplay(
    state: TranscriptState,
    lines: ReadonlyArray<EventStreamLine>,
): ResponseReplayInspection {
    if (
        state.lastDoneReason === null ||
        lines.length === 0 ||
        lines[lines.length - 1]?.type !== 'done'
    ) {
        return { kind: 'not_replay' };
    }
    let doneCount = 0;
    for (const [lineIndex, line] of lines.entries()) {
        if (replayCursorProblem(line.cursor, state.cursor) !== null) {
            return { kind: 'not_replay' };
        }
        switch (line.type) {
            case 'snapshot':
                return { kind: 'not_replay' };
            case 'event': {
                const accepted = state.committedById.get(line.event.id);
                if (
                    !sameCommittedEvent(
                        accepted,
                        retainedCommittedEvent(line.event),
                    )
                ) {
                    return accepted === undefined
                        ? { kind: 'not_replay' }
                        : {
                              kind: 'event_id_conflict',
                              acceptedLineCount: lineIndex,
                              eventType: line.event.type,
                          };
                }
                break;
            }
            case 'pending_event': {
                const accepted = state.pendingById.get(line.pending_event.id);
                if (
                    !equal(accepted, line.pending_event) &&
                    !state.terminalPendingById.has(line.pending_event.id)
                ) {
                    return { kind: 'not_replay' };
                }
                break;
            }
            case 'pending_event_update':
                if (
                    !equal(
                        state.terminalPendingById.get(
                            line.pending_event_update.id,
                        ) ??
                            state.pendingById.get(line.pending_event_update.id),
                        line.pending_event_update,
                    )
                ) {
                    return { kind: 'not_replay' };
                }
                break;
            case 'run_state':
                if (!equal(state.runState, line.run_state)) {
                    return { kind: 'not_replay' };
                }
                break;
            case 'heartbeat':
                break;
            case 'done':
                doneCount += 1;
                if (line.reason !== state.lastDoneReason) {
                    return { kind: 'not_replay' };
                }
                break;
            default:
                return { kind: 'not_replay' };
        }
    }
    return doneCount === 1 ? { kind: 'exact' } : { kind: 'not_replay' };
}

function unexpectedProblem(
    state: TranscriptState,
    lineType: string | null,
): ProtocolProblem {
    return {
        cycleId: state.activeCycleId ?? 'no-active-cycle',
        reason: 'unexpected_exception',
        lineType,
        eventType: null,
    };
}

export function createTranscriptOrigin(
    scope: ScopeDimensions,
    nowMs: number,
): TranscriptState {
    return {
        scope,
        activeCycleId: null,
        cycleStartCursor: ORIGIN_CURSOR,
        cursor: ORIGIN_CURSOR,
        committedById: new Map(),
        eventIdByOffset: new Map(),
        committedOrder: [],
        pendingById: new Map(),
        pendingOrder: [],
        terminalPendingById: new Map(),
        committedIdBySourcePendingId: new Map(),
        sourcePendingIdByCommittedId: new Map(),
        operationIdByPendingId: new Map(),
        operationIdByCommittedId: new Map(),
        optimisticByOperationId: new Map(),
        targetedDrainEvidenceByPendingId: new Map(),
        missingDrainedTargetRecoveryByPendingId: new Map(),
        sequence: [],
        runState: null,
        lifecycle: {
            latestRunStartedEventId: null,
            latestRunStartedAtMs: null,
            latestTerminalEventId: null,
            latestTerminalAtMs: null,
            interruptRequestedAtMs: null,
            pendingWorkObservedAtMs: null,
            runningObservedAtMs: null,
            strongActivityBeganAtMs: null,
            strongHandoffBeganAtMs: null,
        },
        connection: { kind: 'initial', sinceMs: nowMs },
        lastTransportLineAtMs: null,
        lastDoneAtMs: null,
        lastDoneReason: null,
        protocolProblem: null,
        protocolReplayFingerprint: null,
        protocolReplayCount: 0,
        consecutiveNoProgressCycleCount: 0,
    };
}

export function applyStreamLine(
    state: TranscriptState,
    line: EventStreamLine,
    nowMs: number,
): ApplyResult;
export function applyStreamLine(
    state: TranscriptState,
    action: TranscriptAction,
): ApplyResult;
export function applyStreamLine(
    state: TranscriptState,
    line: EventStreamLine | TranscriptAction,
    nowMs?: number,
): ApplyResult {
    if (isAction(line) && line.type === 'scope_reset') {
        const next = createTranscriptOrigin(line.scope, line.nowMs);
        return { ok: true, state: next, acknowledgedCursor: next.cursor };
    }
    if (!isAction(line) && isExactPendingReplay(state, line)) {
        return { ok: true, state, acknowledgedCursor: state.cursor };
    }
    const draft = new TranscriptDraft(state);
    try {
        const problem = isAction(line)
            ? applyLocalAction(draft, line)
            : line.type === 'snapshot'
              ? applySnapshotToDraft(draft, line, nowMs ?? 0)
              : applyWireLine(draft, line, nowMs ?? 0);
        if (problem !== null) {
            return {
                ok: false,
                state,
                acknowledgedCursor: state.cursor,
                problem,
            };
        }
        const next = draft.finalize();
        return { ok: true, state: next, acknowledgedCursor: next.cursor };
    } catch {
        return {
            ok: false,
            state,
            acknowledgedCursor: state.cursor,
            problem: unexpectedProblem(state, line.type),
        };
    }
}

export function applySnapshot(
    state: TranscriptState,
    snapshot: SnapshotLine,
    nowMs: number,
): ApplyResult {
    const draft = new TranscriptDraft(state);
    try {
        const problem = applySnapshotToDraft(draft, snapshot, nowMs);
        if (problem !== null) {
            return {
                ok: false,
                state,
                acknowledgedCursor: state.cursor,
                problem,
            };
        }
        const next = draft.finalize();
        return { ok: true, state: next, acknowledgedCursor: next.cursor };
    } catch {
        return {
            ok: false,
            state,
            acknowledgedCursor: state.cursor,
            problem: unexpectedProblem(state, snapshot.type),
        };
    }
}

function applyResponse(
    state: TranscriptState,
    lines: ReadonlyArray<EventStreamLine>,
    nowMs: number,
): ResponseApplyResult {
    const replay = inspectResponseReplay(state, lines);
    if (replay.kind === 'exact') {
        return {
            ok: true,
            state,
            acknowledgedCursor: state.cursor,
            acceptedLineCount: lines.length,
        };
    }
    if (replay.kind === 'event_id_conflict') {
        const draft = new TranscriptDraft(state);
        const problem = makeProblem(
            draft,
            'event_id_conflict',
            'event',
            replay.eventType,
        );
        recordProblem(draft, problem);
        const next = draft.finalize();
        return {
            ok: false,
            state: next,
            acknowledgedCursor: next.cursor,
            problem,
            acceptedLineCount: replay.acceptedLineCount,
        };
    }
    let draft = new TranscriptDraft(state);
    let acceptedLineCount = 0;
    let problem: ProtocolProblem | null = null;
    let sawDone = false;
    for (const line of lines) {
        try {
            if (line.type === 'snapshot') {
                if (acceptedLineCount > 0) {
                    problem = makeProblem(
                        draft,
                        'unexpected_snapshot',
                        line.type,
                    );
                } else {
                    const snapshotDraft = new TranscriptDraft(state);
                    problem = applySnapshotToDraft(snapshotDraft, line, nowMs);
                    if (problem === null) {
                        draft = snapshotDraft;
                    }
                }
            } else {
                problem = applyWireLine(draft, line, nowMs);
            }
        } catch {
            problem = makeProblem(draft, 'unexpected_exception', line.type);
        }
        if (problem !== null) {
            break;
        }
        acceptedLineCount += 1;
        sawDone = line.type === 'done' || sawDone;
    }
    if (problem === null && !sawDone) {
        problem = makeProblem(draft, 'missing_done', null);
    }
    if (problem !== null) {
        recordProblem(draft, problem);
        const next = draft.finalize();
        return {
            ok: false,
            state: next,
            acknowledgedCursor: next.cursor,
            problem,
            acceptedLineCount,
        };
    }
    const next = draft.finalize();
    return {
        ok: true,
        state: next,
        acknowledgedCursor: next.cursor,
        acceptedLineCount,
    };
}

export function applyStreamResponse(
    state: TranscriptState,
    lines: ReadonlyArray<EventStreamLine>,
    nowMs: number,
): ResponseApplyResult {
    try {
        return applyResponse(state, lines, nowMs);
    } catch {
        const problem = unexpectedProblem(state, null);
        return {
            ok: false,
            state,
            acknowledgedCursor: state.cursor,
            problem,
            acceptedLineCount: 0,
        };
    }
}
